/**
 * The title screen plane carousel: five planes in formation flying a steady
 * circle below the camera, with random explosions.
 *
 * The camera sits on the orbit and the formation is pinned below it, so the
 * orbit itself is never visible: what shows is the map sliding under the
 * planes and the planes turning to match. Legacy orbited at a radius that
 * swung between 100 and 2000 while holding the ground speed fixed, which made
 * the turn rate swing twentyfold over a two minute cycle, and the radial
 * motion offset the heading on top of that. A fixed radius gives one steady
 * turn rate, and the heading follows from the orbit angle directly.
 */
import { PLAYER_STATUS } from '@airmash/protocol';
import { clock } from './Clock';
import { HALF_PI, PlaneType, TWO_PI } from './Constants';
import { setCamera as graphicsSetCamera, viewport } from './Graphics';
import {
  explosion as particlesExplosion,
  update as particlesUpdate,
  wipe as particlesWipe,
} from './particles/Particles';
import type Player from './Player';
import { add as playersAdd, update as playersUpdate, wipe as playersWipe } from './Players';
import { rand, randInt } from './Tools';
import type { ReelPlayer } from './types/Players';
import Vector from './Vector';

/** The formation, left to right, with each plane's horizontal offset from the camera. */
const REEL_PLANES: readonly { type: PlaneType; offset: number }[] = [
  { type: PlaneType.Mohawk, offset: -270 },
  { type: PlaneType.Predator, offset: -150 },
  { type: PlaneType.Goliath, offset: 0 },
  { type: PlaneType.Tornado, offset: 150 },
  { type: PlaneType.Prowler, offset: 270 },
];

// Orbit centre, in world coordinates.
const REEL_START_X = 200;
const REEL_START_Y = -2450;
/** How far below the camera the formation sits, as a fraction of the visible height. */
const REEL_PLAYER_VERTICAL_OFFSET = 0.24;
/** Ground speed in world units per frame: legacy's speed along the orbit. */
const REEL_SPEED = 1;
/** Seconds per full turn of the formation. */
const REEL_TURN_PERIOD_S = 30;
/** Turn rate in radians per frame. */
const REEL_TURN_RATE = TWO_PI / (REEL_TURN_PERIOD_S * 60);
/** The radius at which REEL_TURN_RATE moves the formation at REEL_SPEED. */
const REEL_ORBIT_RADIUS = REEL_SPEED / REEL_TURN_RATE;
const REEL_MIN_EXPLOSION_DELAY = 1000;
const REEL_MAX_EXPLOSION_DELAY = 3000;
const REEL_EXPLOSION_OFFSET_RANGE = 100;

/** A plane in the formation and its offset from the camera. */
interface ReelPlane {
  player: Player;
  offset: number;
}

/** Title screen carousel state. */
interface ReelState {
  /** The formation, empty until the first frame creates it. */
  planes: ReelPlane[];
  /** Orbit angle, in radians. */
  pan: number;
  /** clock.time after which the next explosion fires. */
  explosion: number;
}

/** The reel as it is before its first frame, and again after wipeReel. */
function createReelState(): ReelState {
  return {
    planes: [],
    pan: 0,
    explosion: 4e3,
  };
}

let reelState = createReelState();

/** Adds the formation's planes, thrust on so their exhaust shows. */
function createPlanes(): ReelPlane[] {
  return REEL_PLANES.map(({ type, offset }, index) => {
    const reelPlayer: ReelPlayer = {
      id: index + 1,
      team: 1,
      status: PLAYER_STATUS.ALIVE,
      reel: true,
      name: '',
      type,
      posX: 0,
      posY: 0,
      rot: 0,
      flag: 1,
    };
    const player = playersAdd(reelPlayer);
    player.keystate.UP = true;
    return { player, offset };
  });
}

/** Animates the title screen plane carousel: the orbit, the formation and the explosions. */
export function updateReel() {
  if (reelState.planes.length === 0) {
    reelState.planes = createPlanes();
  }

  reelState.pan = (reelState.pan + REEL_TURN_RATE * clock.timeFactor) % TWO_PI;

  // Camera on the orbit
  const posX = REEL_START_X + Math.sin(reelState.pan) * REEL_ORBIT_RADIUS;
  const posY = REEL_START_Y - Math.cos(reelState.pan) * REEL_ORBIT_RADIUS;

  // The orbit's tangent points at angle pan. A sprite at rotation 0 points up,
  // a quarter turn short of angle 0, so the heading is pan plus a quarter turn.
  const heading = reelState.pan + HALF_PI;

  graphicsSetCamera(posX, posY);
  playersUpdate();
  particlesUpdate();

  // The formation sits a fixed way below the camera and shares its heading
  const planeY = posY + (viewport.height / viewport.scale) * REEL_PLAYER_VERTICAL_OFFSET;
  for (const { player, offset } of reelState.planes) {
    player.pos.x = posX + offset;
    player.pos.y = planeY;
    player.rot = heading;
  }

  // Trigger random explosions
  if (clock.time > reelState.explosion) {
    const viewportHalfWidth = viewport.halfWidth / viewport.scale;
    const viewportHalfHeight = viewport.halfHeight / viewport.scale;

    const explosionPos = new Vector(
      rand(posX - viewportHalfWidth, posX + viewportHalfWidth),
      rand(posY - viewportHalfHeight, posY + viewportHalfHeight),
    );

    // Primary explosion
    particlesExplosion(explosionPos, rand(2, 2.5), randInt(4, 7));

    // Secondary offset explosion
    const offsetExplosionPos = new Vector(
      explosionPos.x + rand(-REEL_EXPLOSION_OFFSET_RANGE, REEL_EXPLOSION_OFFSET_RANGE),
      explosionPos.y + rand(-REEL_EXPLOSION_OFFSET_RANGE, REEL_EXPLOSION_OFFSET_RANGE),
    );
    particlesExplosion(offsetExplosionPos, rand(1, 1.2));

    // Schedule next explosion
    reelState.explosion = clock.time + rand(REEL_MIN_EXPLOSION_DELAY, REEL_MAX_EXPLOSION_DELAY);
  }
}

/** Tears down the title screen reel and resets state for potential restart. */
export function wipeReel() {
  particlesWipe();
  playersWipe();

  // Back to the initial state, or returning to the title screen would not recreate the planes
  reelState = createReelState();
}
